import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ThemeToggle, LanguageToggle } from "@/components/Toggles";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { LogOut, Menu, X } from "lucide-react";

export const DashboardLayout = ({ children, items = [], title }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 z-50 w-64 bg-[var(--so-surface)] border-e border-[var(--so-border)] transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 px-5 flex items-center justify-between border-b border-[var(--so-border)]">
          <Link to="/"><Logo size={28} /></Link>
          <button className="lg:hidden" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <nav className="p-3 space-y-1">
          {items.map(({ to, icon: Icon, label, end, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={tid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[var(--so-orange)]/10 text-[var(--so-orange)] border-s-2 border-[var(--so-orange)]"
                    : "text-[var(--so-text-soft)] hover:bg-[var(--so-bg)]"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-3 border-t border-[var(--so-border)]">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-[var(--so-text-soft)]">{user?.role}</div>
            <div className="font-semibold text-sm truncate">{user?.name}</div>
          </div>
          <button onClick={handleLogout} data-testid="dashboard-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[var(--so-text-soft)] hover:bg-[var(--so-bg)]">
            <LogOut className="w-4 h-4" />
            <span>{t("logout")}</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 px-4 lg:px-8 flex items-center justify-between border-b border-[var(--so-border)] bg-[var(--so-surface)]">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open"><Menu className="w-5 h-5" /></button>
            <h1 className="text-lg font-bold">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 bg-[var(--so-bg)]">{children}</main>
      </div>
    </div>
  );
};
